import type { NotificationGroup, NotificationType } from '@unipostudio/shared-types';
import { api } from './api';

// One list for the API, the worker and the web (N21): a type the server
// writes always has an icon and a filter group here.
export type { NotificationGroup, NotificationType } from '@unipostudio/shared-types';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  read: boolean;
  link: string | null;
  /** What the server stored with it; occasion reminders carry the occasion here. */
  data?: Record<string, unknown> | null;
  createdAt: string;
  /** The brand it is from; null for an account-wide notice. */
  workspaceId?: string | null;
  workspaceName?: string | null;
}

export interface NotificationListParams {
  /** The bell's default (the server's too) is unread only; the history asks for false. */
  unreadOnly?: boolean;
  page?: number;
  limit?: number;
  group?: NotificationGroup;
  /** Only this brand's. */
  workspaceId?: string;
}

export interface NotificationList {
  data: Notification[];
  /** Every notification the filter matches, for "load more". */
  total?: number;
  /** Every unread notification, for the badge. */
  unreadCount: number;
}

export const notificationsService = {
  async list(params?: NotificationListParams): Promise<NotificationList> {
    const searchParams = new URLSearchParams();
    if (params?.unreadOnly !== undefined) searchParams.set('unreadOnly', String(params.unreadOnly));
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.group) searchParams.set('group', params.group);
    if (params?.workspaceId) searchParams.set('workspaceId', params.workspaceId);
    const res = await api.get(`/notifications?${searchParams.toString()}`);
    return res.data;
  },

  async markRead(id: string): Promise<void> {
    await api.patch(`/notifications/${id}/read`);
  },

  /** Several at once (the history's "Mark these read"); at most 100 ids. */
  async markManyRead(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    await api.patch('/notifications/read', { ids: ids.slice(0, 100) });
  },

  async markAllRead(workspaceId?: string): Promise<void> {
    await api.patch(workspaceId ? `/notifications/read-all?workspaceId=${encodeURIComponent(workspaceId)}` : '/notifications/read-all');
  },
};
